import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router';

import { useUrlFromHandle } from '../../contexts/RoutesContext';
import { useSearch } from '../../contexts/SearchContext';
import { useMenu } from '../../contexts/SiteContext';
import partners from '../../data/partners';
import PillButton from '../buttons/Pill';
import NewsletterEmail from '../forms/NewsletterEmail';
import SearchBar from '../forms/SearchBar';
import SocialIcon from '../icons/Social';
import Menu from '../menus/Menu';
import Image from '../partials/Image';
import Link from '../partials/Link';

import styles from '../../../styles/layouts/footer.module.scss';

const propTypes = {
    hidePartners: PropTypes.bool,
    className: PropTypes.string,
};

const defaultProps = {
    hidePartners: true,
    className: null,
};

const Footer = ({ hidePartners, className }) => {
    const { footer = [], social = [], bottom = [] } = useMenu();
    const history = useHistory();
    const routeByHandle = useUrlFromHandle();
    const { setValue: setGlobalSearchValue } = useSearch();
    const [searchValue, setSearchValue] = useState();

    const onSearchChange = useCallback(
        (value) => {
            setSearchValue(value);
        },
        [setSearchValue],
    );

    const onSearchSubmit = useCallback(
        (e) => {
            e.preventDefault();
            document.activeElement.blur();
            setGlobalSearchValue(searchValue);
            setSearchValue(null);
            history.push(`${routeByHandle('search')}?search=${encodeURIComponent(searchValue)}`);
        },
        [searchValue, setGlobalSearchValue, setSearchValue, history, routeByHandle],
    );

    return (
        <footer
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
        >
            <div className={styles.sectionNavigation}>
                <div className={classNames(styles.inner, styles.innerNavigation)}>
                    <div className={styles.columns}>
                        {footer.map((items, i) => (
                            <div key={`col-${i + 1}`} className={styles.col}>
                                {items.map(
                                    (
                                        {
                                            label = null,
                                            url = null,
                                            items: subItems = null,
                                            isIndex = false,
                                        },
                                        j,
                                    ) => {
                                        const labelComponent = (
                                            <span className={styles.sectionLabel}>{label}</span>
                                        );
                                        return (
                                            <div key={`${url}-${j + 1}`} className={styles.section}>
                                                {!isIndex ? (
                                                    <Link className={styles.sectionLink} href={url}>
                                                        {labelComponent}
                                                    </Link>
                                                ) : (
                                                    labelComponent
                                                )}
                                                {subItems !== null && subItems.length > 0 ? (
                                                    <Menu
                                                        items={subItems}
                                                        className={styles.menu}
                                                        itemsClassName={styles.items}
                                                        itemClassName={styles.item}
                                                        linkClassName={styles.link}
                                                        activeItemClassName={styles.activeItem}
                                                    />
                                                ) : null}
                                            </div>
                                        );
                                    },
                                )}
                            </div>
                        ))}
                    </div>
                    <SearchBar
                        className={styles.search}
                        value={searchValue}
                        onChange={onSearchChange}
                        onSubmit={onSearchSubmit}
                    />
                </div>
            </div>
            <div className={styles.sectionSocial}>
                <div className={classNames(styles.inner, styles.innerSocial)}>
                    <NewsletterEmail className={styles.newsletter} />
                    <div className={styles.right}>
                        <div className={styles.socialMediaShortcuts}>
                            {social.map((link) => (
                                <PillButton
                                    key={link.id}
                                    href={link.url}
                                    external
                                    target="_blank"
                                    className={classNames([
                                        styles.socialMediaLink,
                                        styles.withBackground,
                                    ])}
                                    icon={
                                        <SocialIcon
                                            type={link.provider}
                                            className={styles.socialMediaIcon}
                                        />
                                    }
                                    isDark
                                    title={link.label}
                                />
                            ))}
                        </div>
                        <div className={styles.bottomPages}>
                            {bottom.map((item) => (
                                <Link key={item.id} href={item.url} className={styles.bottomPage}>
                                    {item.label}
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {!hidePartners && partners.length > 0 ? (
                <div className={styles.sectionPartners}>
                    <div className={classNames(styles.inner, styles.innerPartners)}>
                        <div className={styles.partners}>
                            {partners.map((partner) => (
                                <Link
                                    key={partner.id}
                                    href={partner.url}
                                    className={styles.partnerLink}
                                    external
                                >
                                    <Image
                                        media={partner.image}
                                        className={styles.partnerImage}
                                        imageClassName={styles.partnerLogo}
                                    />
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            ) : null}
        </footer>
    );
};
Footer.propTypes = propTypes;
Footer.defaultProps = defaultProps;

export default Footer;
